import {
  FutureActivityDisabled,
  FutureActivityResponse
} from '../../models/future-activites/future-activites.interfaces';
import { AEMErrorMessage } from '../../models/status-tracker/aem/aem-status-tracker.interfaces';
import {
  LoyaltyMemberTierLevel,
  StatusTrackerResponse,
  Threshold
} from '../../models/status-tracker/api/api-status-tracker.interfaces';
import {
  StatusTracker,
  Tracker as TrackerContent
} from '../../models/status-tracker/status-tracker.interfaces';
import {
  checkErrors,
  getEmptyContent,
  getLink,
  logError
} from '../../services/utils';
import {
  isDisplay,
  isError,
  isValidData
} from '../../services/utils/validators/structures/predicates.utils';
import { ActionType } from '../../types/common.types';
import { getEarnometer } from '../../utils/status-tracker/earnometer.utils';
import {
  getJourney,
  getNextTierName
} from '../../utils/status-tracker/journey.utils';
import { getLegends } from '../../utils/status-tracker/legends.utils';
import {
  getHeader,
  setInitialData
} from '../../utils/status-tracker/status-tracker.utils';

export const getTracker = (
  tracker: StatusTrackerResponse | undefined,
  futureActivities: FutureActivityResponse | FutureActivityDisabled | undefined,
  aem: TrackerContent | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): StatusTracker => {
  try {
    const futureActivityError = checkFutureActivity(errors);
    if (!futureActivityError && checkErrors(errors, aemErrorMessages)) {
      return isError<StatusTracker>(
        'tracker',
        aemErrorMessages as AEMErrorMessage[],
        true
      );
    }
    return checkData(
      tracker,
      aem,
      futureActivityError,
      futureActivities,
      aemErrorMessages
    );
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'TRACKER');
    return getEmptyContent<StatusTracker>(
      'tracker',
      aemErrorMessages,
      true,
      true
    );
  }
};

const checkData = (
  tracker: StatusTrackerResponse | undefined,
  aem: TrackerContent | undefined,
  futureActivityError: boolean,
  futureActivities: FutureActivityResponse | FutureActivityDisabled | undefined,
  aemErrorMessages: AEMErrorMessage[] | undefined
) => {
  return isValidData<
    string,
    [
      StatusTrackerResponse | undefined,
      TrackerContent | undefined,
      boolean | undefined
    ]
  >('loyaltyMemberId', tracker, aem, true) &&
    (futureActivityError || futureActivities)
    ? adaptTrackerObject(
        tracker as StatusTrackerResponse,
        aem as TrackerContent,
        futureActivities as FutureActivityResponse | undefined,
        aemErrorMessages as AEMErrorMessage[]
      )
    : isDisplay<StatusTracker>(true);
};
const adaptTrackerObject = (
  api: StatusTrackerResponse,
  aem: TrackerContent,
  futureActivities: FutureActivityResponse | undefined,
  aemErrorMessages: AEMErrorMessage[]
): StatusTracker => {
  const { futureMedallionData, tier, pending, mqdThreshold } = setInitialData(
    futureActivities,
    api
  );
  if (futureMedallionData) {
    return {
      header: getHeader(futureMedallionData as LoyaltyMemberTierLevel, aem),
      earnometer: getEarnometer(mqdThreshold, aem, pending, tier),
      journey: getJourney(
        mqdThreshold as Threshold,
        aem,
        getNextTierName(api.currentLoyaltyMemberMedallionStatus),
        pending
      ),
      legends: getLegends(tier, aem.legends),
      timeFrame: aem.period,
      action: {
        type: 'text' as ActionType,
        link: getLink(aem.actionLabel, '')
      },
      loaded: true,
      disabled: false,
      ariaLabel: '',
      educationalTitle: aem.educationalTitle
    };
  } else {
    return getEmptyContent<StatusTracker>(
      'tracker',
      aemErrorMessages,
      true,
      true
    );
  }
};

const checkFutureActivity = (errors: (string | undefined)[]): boolean => {
  return !!errors.find((error) => error === 'future-activity');
};
